import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, forkJoin, timeout, TimeoutError } from 'rxjs';
import { map, catchError, switchMap, mergeMap, tap, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DocumentService } from '../services/document.service';
import * as DocumentActions from './document.actions';

/**
 * Document Effects
 * Handles side effects for document actions
 */
@Injectable()
export class DocumentEffects {
  private actions$ = inject(Actions);
  private documentService = inject(DocumentService);
  private router = inject(Router);

  /**
   * Load documents list effect
   * Uses debounceTime to prevent rapid duplicate requests within 100ms
   * Uses timeout to prevent infinite loading state
   *
   * NOTE: Removed distinctUntilChanged because it was preventing re-fetch on navigation
   * When user navigates away and back, we need to re-fetch even with the same filters
   */
  loadDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.loadDocuments),
      tap(action => console.log('[Documents Effect] loadDocuments action received:', action.filters)),
      debounceTime(100), // Debounce rapid requests by 100ms (prevents double-clicks)
      // Removed: distinctUntilChanged - was causing bug where documents wouldn't reload after navigation
      tap(() => console.log('[Documents Effect] Making API call to fetch documents...')),
      switchMap(({ filters }) =>
        this.documentService.getDocuments(filters).pipe(
          timeout(30000), // 30 second timeout to prevent infinite loading
          tap(data => console.log('[Documents Effect] API call successful, received data:', data)),
          map(data => {
            console.log('[Documents Effect] Dispatching loadDocumentsSuccess');
            // Transform items to add 'id' from '_id' for frontend compatibility
            const transformedData = {
              ...data,
              items: data.items.map(item => ({
                ...item,
                id: item._id || item.id // Ensure 'id' is available for templates
              }))
            };
            return DocumentActions.loadDocumentsSuccess({ data: transformedData });
          }),
          catchError(error => {
            console.error('[Documents Effect] Error loading documents:', error);

            // Handle timeout errors specifically
            if (error instanceof TimeoutError) {
              console.error('[Documents Effect] Request timed out after 30 seconds');
              return of(DocumentActions.loadDocumentsFailure({
                error: 'Request timed out. Please try again.'
              }));
            }

            // Handle other errors
            const errorMessage = error?.error?.detail || error?.message || 'Failed to load documents';
            console.error('[Documents Effect] Dispatching loadDocumentsFailure with error:', errorMessage);
            return of(DocumentActions.loadDocumentsFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  /**
   * Load single document effect
   */
  loadDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.loadDocument),
      switchMap(({ documentId }) =>
        this.documentService.getDocument(documentId).pipe(
          map(document => DocumentActions.loadDocumentSuccess({ document })),
          catchError(error => {
            console.error('Error loading document:', error);
            return of(DocumentActions.loadDocumentFailure({ 
              error: error.message || 'Failed to load document' 
            }));
          })
        )
      )
    )
  );

  /**
   * Upload document effect (multipart for files <= 10MB)
   */
  uploadDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.uploadDocument),
      mergeMap(({ file, templateId, tags }) =>
        this.documentService.uploadDocument(file, templateId, tags).pipe(
          mergeMap(result => {
            if (result.response) {
              return [
                DocumentActions.uploadDocumentProgress({ file, progress: 100 }),
                DocumentActions.uploadDocumentSuccess({ response: result.response })
              ];
            } else {
              return [
                DocumentActions.uploadDocumentProgress({ file, progress: result.progress })
              ];
            }
          }),
          catchError(error => {
            console.error('Error uploading document:', error);
            return of(DocumentActions.uploadDocumentFailure({ 
              file,
              error: error.error?.detail || error.message || 'Failed to upload document' 
            }));
          })
        )
      )
    )
  );

  /**
   * Upload large document effect (presigned URL for files > 10MB)
   */
  uploadLargeDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.uploadLargeDocument),
      mergeMap(({ file, templateId, tags }) => {
        // Step 1: Generate presigned URL
        const request = {
          filename: file.name,
          file_size: file.size,
          mime_type: file.type,
          template_id: templateId,
          tags
        };

        return this.documentService.generatePresignedUrl(request).pipe(
          switchMap(presignedResponse => {
            // Step 2: Upload to S3
            return this.documentService.uploadToS3(
              presignedResponse.upload_url,
              presignedResponse.upload_fields,
              file
            ).pipe(
              mergeMap(progress => {
                if (progress === 100) {
                  // Step 3: Confirm upload
                  return this.documentService.confirmUpload(
                    presignedResponse.document_id,
                    { etag: 'placeholder' } // S3 should return ETag in response headers
                  ).pipe(
                    map(() => DocumentActions.uploadLargeDocumentSuccess({ 
                      file, 
                      documentId: presignedResponse.document_id 
                    }))
                  );
                } else {
                  return of(DocumentActions.uploadLargeDocumentProgress({ file, progress }));
                }
              })
            );
          }),
          catchError(error => {
            console.error('Error uploading large document:', error);
            return of(DocumentActions.uploadLargeDocumentFailure({ 
              file,
              error: error.error?.detail || error.message || 'Failed to upload large document' 
            }));
          })
        );
      })
    )
  );

  /**
   * Navigate to document detail after successful upload
   */
  navigateAfterUploadSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.uploadDocumentSuccess),
      tap(({ response }) => {
        console.log('[Documents Effect] Upload successful, navigating to document detail:', response.document_id);
        this.router.navigate(['/documents', response.document_id]);
      })
    ),
    { dispatch: false }
  );

  /**
   * Navigate to document detail after successful large upload
   */
  navigateAfterLargeUploadSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.uploadLargeDocumentSuccess),
      tap(({ documentId }) => {
        console.log('[Documents Effect] Large upload successful, navigating to document detail:', documentId);
        this.router.navigate(['/documents', documentId]);
      })
    ),
    { dispatch: false }
  );

  /**
   * Delete document effect
   */
  deleteDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.deleteDocument),
      switchMap(({ documentId, permanent }) =>
        this.documentService.deleteDocument(documentId, permanent).pipe(
          map(() => DocumentActions.deleteDocumentSuccess({ documentId })),
          catchError(error => {
            console.error('Error deleting document:', error);
            return of(DocumentActions.deleteDocumentFailure({
              error: error.message || 'Failed to delete document'
            }));
          })
        )
      )
    )
  );

  /**
   * Bulk delete documents effect
   */
  bulkDeleteDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.bulkDeleteDocuments),
      switchMap(({ documentIds, permanent }) => {
        // Delete all documents in parallel
        const deleteRequests = documentIds.map(id =>
          this.documentService.deleteDocument(id, permanent)
        );

        return forkJoin(deleteRequests).pipe(
          map(() => DocumentActions.bulkDeleteDocumentsSuccess({ documentIds })),
          catchError(error => {
            console.error('Error bulk deleting documents:', error);
            return of(DocumentActions.bulkDeleteDocumentsFailure({
              error: error.message || 'Failed to delete documents'
            }));
          })
        );
      })
    )
  );

  /**
   * Load document detail effect
   */
  loadDocumentDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.loadDocumentDetail),
      switchMap(({ documentId }) =>
        this.documentService.getDocument(documentId).pipe(
          map(document => DocumentActions.loadDocumentDetailSuccess({
            document: { ...document, id: document._id }
          })),
          catchError(error => {
            console.error('Error loading document detail:', error);
            return of(DocumentActions.loadDocumentDetailFailure({
              error: error.message || 'Failed to load document details'
            }));
          })
        )
      )
    )
  );
}

